import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  VerifiedUser as VerifiedIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QuoteRequestForm from './QuoteRequestForm.jsx';
import RouteMap from '../components/RouteMap.jsx';
import WhyChooseUsSection from '../components/WhyChooseUsSection';
import TestimonialsSection from '../components/TestimonialsSection';
import ServicesSection from '../components/ServicesSection';
import StorageCrossSell from '../components/StorageCrossSell.jsx';

// Trust indicators data
const trustStats = [
  { icon: <TrendingUpIcon />, value: '10,000+', label: 'Happy Customers' },
  { icon: <SecurityIcon />, value: '100%', label: 'Insured Moves' },
  { icon: <SpeedIcon />, value: '24/7', label: 'Customer Support' },
  { icon: <VerifiedIcon />, value: '5 Star', label: 'Average Rating' }
];

// How it works steps
const howItWorksSteps = [
  {
    step: 1,
    icon: <PhoneIcon />,
    title: 'Get a Quote',
    description: 'Fill out our simple form and get an instant moving quote tailored to your needs'
  },
  {
    step: 2,
    icon: <EmailIcon />,
    title: 'Book Your Move',
    description: 'Choose your preferred date and time. We\'ll handle all the coordination for you'
  },
  {
    step: 3,
    icon: <ArrowForwardIcon />,
    title: 'Relax & Move',
    description: 'Our professional team handles everything from packing to delivery with care'
  }
];

const ProfessionalHomePage = () => {
  const navigate = useNavigate();
  
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  const handleGetStarted = () => {
    navigate('/quote');
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section - Call to Action */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          color: 'white',
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
        
        <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
          {/* Call to Action Header */}
          <Box textAlign="center" mb={6}>
            <Box>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  lineHeight: 1.1,
                  mb: 3
                }}
              >
                Get Your Moving Quote
                <Box component="span" sx={{ color: '#38bdf8' }}> Instantly</Box>
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.4,
                  fontWeight: 400,
                  maxWidth: '800px',
                  mx: 'auto'
                }}
              >
                Enter your moving details, visualize your route, and get accurate pricing in real-time. 
                Canada's most trusted moving platform makes it simple.
              </Typography>

              {/* Trust Indicators */}
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                {trustStats.map((stat, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: '#38bdf8' }}>{stat.icon}</Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{stat.value}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>{stat.label}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Quote Request & Route Map Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'grey.50', mt: -2, position: 'relative', zIndex: 2 }}>
        <Box 
          sx={{ 
            maxWidth: '100vw',
            width: '100%',
            px: { xs: 2, md: 4 }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            gap: 4, 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            width: '100%'
          }}>
            {/* Quote Request Form */}
            <Box sx={{ flex: 1, display: 'flex', minWidth: { xs: '100%', md: '45%' } }}>
              <Paper
                elevation={8}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  flex: 1
                }}
              >
                <Typography variant="h4" fontWeight={700} mb={3} color="primary.main" textAlign="center">
                  Step 1: Enter Your Details
                </Typography>
                <Box sx={{
                  flexGrow: 1,
                  '& > *': {
                    width: '100% !important',
                    maxWidth: 'none !important',
                    minWidth: '100% !important'
                  }
                }}>
                  <QuoteRequestForm
                    userId="demo-user-id"
                    token="demo-token"
                    noPaper
                    origin={origin}
                    destination={destination}
                    setOrigin={setOrigin}
                    setDestination={setDestination}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Route Map */}
            <Box sx={{ flex: 1, display: 'flex', minWidth: { xs: '100%', md: '45%' } }}>
              <Paper
                elevation={8}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  height: '100%',
                  width: '100%',
                  minHeight: { xs: 400, md: 500 },
                  display: 'flex',
                  flexDirection: 'column',
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  flex: 1
                }}
              >
                <Typography variant="h4" fontWeight={700} mb={3} color="secondary.main" textAlign="center">
                  Step 2: See Your Route
                </Typography>
                <Box sx={{ 
                  flexGrow: 1, 
                  position: 'relative', 
                  minHeight: 450, 
                  width: '100%', 
                  overflow: 'hidden',
                  '& > *': {
                    width: '100% !important',
                    maxWidth: 'none !important'
                  }
                }}>
                  <RouteMap
                    origin={origin || 'Vancouver, BC, Canada'}
                    destination={destination || 'Toronto, ON, Canada'}
                    height={400}
                    style={{
                      margin: 0,
                      borderRadius: '12px',
                      width: '100%',
                      maxWidth: '100%'
                    }}
                  />
                  <Box sx={{ height: 50 }} />
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Call to Action Buttons */}
          <Box textAlign="center" mt={6}>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  px: 8,
                  py: 2.5,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: 4,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Continue to Full Quote →
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/how-it-works')}
                sx={{
                  px: 6,
                  py: 2.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                How It Works
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Quick How It Works Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
        <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight={700} mb={3}>
              What Happens Next?
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Complete your quote in just a few more steps
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {howItWorksSteps.map((step, index) => (
              <Box key={step.step} sx={{ flex: 1, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    bgcolor: step.step === 1 ? 'primary.main' : step.step === 2 ? 'secondary.main' : 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    fontSize: 32
                  }}
                >
                  {step.icon}
                </Box>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Step {step.step}: {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Why Choose Us Section */}
      <WhyChooseUsSection />

      {/* Services Section */}
      <ServicesSection />

      {/* Storage Cross-Sell Section */}
      <Box sx={{ py: 4, bgcolor: 'background.default' }}>
        <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, md: 4 } }}>
          <StorageCrossSell context="moving" />
        </Box>
      </Box>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Trust Stats Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: { xs: 6, md: 8 } }}>
        <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight={700} mb={3}>
              Trusted by Thousands of Canadians
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, textAlign: 'center' }}>
            {trustStats.map((stat, index) => (
              <Box key={index} sx={{ flex: 1 }}>
                <Box sx={{ mb: 2, fontSize: 48 }}>{stat.icon}</Box>
                <Typography variant="h3" fontWeight={800} mb={1}>
                  {stat.value}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfessionalHomePage;